import React, { useState } from 'react';
import styled from 'styled-components';

import {
  activateReportsAddon,
  createReportsAddonBundleCheckout,
} from '../../pages/player/api';

// Reports Add-on PRD §7.1 — bottom sheet with two modes:
//
//   mode="addon"  — for users with an active main subscription. One CTA
//                   hits POST /subscription/reports-addon (off-session),
//                   parent refetches subscription, then re-triggers the
//                   download.
//
//   mode="bundle" — for trial-exhausted users. CTA hits the bundle
//                   Checkout endpoint and redirects to Stripe. Parent
//                   sees the return URL post-Checkout and triggers the
//                   download from its Dashboard query-param handler.
//
//   mode="unavailable" (Track B / unknown) — copy-only, no CTA.
//
// onActivated() is called after a successful off-session activation so
// the parent can refetch subscription state and re-fire the download.
const ReportsUpgradeSheet = ({ open, mode, onClose, onActivated }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const onActivate = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await activateReportsAddon();
      onActivated?.();
      onClose?.();
    } catch (e) {
      const code = e?.response?.data?.code;
      if (code === 'ADDON_PAYMENT_REQUIRED') {
        setError(
          'Your card on file was declined. Please update it in Billing and try again.'
        );
      } else if (code === 'BUNDLE_CHECKOUT_REQUIRED') {
        setError('Your main subscription needs renewing first. Please refresh and try again.');
      } else {
        setError(
          e?.response?.data?.message || 'Could not activate Reports. Please try again.'
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const onBundle = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/player/dashboard`;
      const res = await createReportsAddonBundleCheckout(returnUrl, 'monthly');
      const url = res?.data?.url;
      if (!url) throw new Error('No checkout URL');
      window.location.assign(url);
    } catch (e) {
      setBusy(false);
      setError(
        e?.response?.data?.message || 'Could not start checkout. Please try again.'
      );
    }
  };

  const isAddon = mode === 'addon';
  const isBundle = mode === 'bundle';

  return (
    <Backdrop onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()} role="dialog" aria-modal>
        <Handle />
        {isAddon && (
          <>
            <Title>Unlock your Player Reports</Title>
            <Body>
              To access your full-season Player Reports, please subscribe to
              the Player Report package — <strong>€10/month</strong>, charged
              to the card you already have on file. Cancel anytime from Billing.
            </Body>
            <PrimaryButton type="button" onClick={onActivate} disabled={busy}>
              {busy ? 'Activating…' : 'Add Reports — €10/mo'}
            </PrimaryButton>
          </>
        )}
        {isBundle && (
          <>
            <Title>Upgrade to keep your reports</Title>
            <Body>
              Your trial report has been used. Upgrade now and Reports stays
              unlocked for the full season: <strong>€69 main + €10 reports
              = €79/month</strong>, billed once your trial ends.
            </Body>
            <PrimaryButton type="button" onClick={onBundle} disabled={busy}>
              {busy ? 'Opening checkout…' : 'Continue to checkout'}
            </PrimaryButton>
          </>
        )}
        {!isAddon && !isBundle && (
          <>
            <Title>Reports unavailable</Title>
            <Body>
              Reports are managed by your team admin or club. Please ask them
              to enable Reports on your seat.
            </Body>
          </>
        )}
        {error && <ErrorText role="alert">{error}</ErrorText>}
        <SecondaryButton type="button" onClick={onClose}>Not now</SecondaryButton>
      </Sheet>
    </Backdrop>
  );
};

export default ReportsUpgradeSheet;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
  padding: 0;
  @media (min-width: 600px) { align-items: center; padding: 24px; }
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 520px;
  background: ${(p) => p.theme.row};
  color: ${(p) => p.theme.white};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding: 16px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (min-width: 600px) { border-radius: 16px; }
`;

const Handle = styled.div`
  width: 44px;
  height: 4px;
  background: ${(p) => p.theme.borderColor};
  border-radius: 2px;
  align-self: center;
  margin-bottom: 4px;
`;

const Title = styled.div`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 18px;
`;

const Body = styled.div`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${(p) => p.theme.white};
  opacity: 0.9;
`;

const PrimaryButton = styled.button`
  margin-top: 4px;
  padding: 12px 16px;
  background: ${(p) => p.theme.green};
  color: ${(p) => p.theme.black};
  border: 0;
  border-radius: 8px;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
  cursor: pointer;
  &:disabled { opacity: 0.7; cursor: default; }
`;

const SecondaryButton = styled.button`
  padding: 8px 12px;
  background: transparent;
  color: ${(p) => p.theme.white};
  border: 1px solid ${(p) => p.theme.borderColor};
  border-radius: 8px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  cursor: pointer;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.red};
  font-family: 'SatoshiMedium', sans-serif;
`;
